import { CellContext } from "@tanstack/react-table";
import { CMSController, FetchPostsFilter, FileEntrySearchFilter, FileEntrySearchResultDto, PALMSController, PALMSModule, PostController, PostDownloadDto, ProjectGroupSearch, SearchResultsDto, TubePostDownloadDto, UserShortInfoDownloadDto, WikiController } from "collaboration-service";
import { ColumnDefintion } from "components/LoadingDataTable/LoadingDataTable";
import SafeHTML from "components/SafeHTML/SafeHTML";
import { Icon, Image } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { useAppSelector } from "services/ApplicationState/ApplicationState";
import { reducerBatchJob, reducerSetIfChanged } from "services/Helpers";
import { getThumbnailOrMediaLink } from "services/Helpers/MediaHelpers";
import { getTranslatedStandardDate } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { UseCMSSearchRenderFileVersions, UseCMSSearchRenderPeview } from "views/CMS/useCMSSearch/useCMSSearchComponents";
import CMSFilter from "./Filters/CMSFilter";
import StandardFilter from "./Filters/StandardFilter";

export const postTypesElements: PostTypes[] =
    ["TubePost",
        "Quiz",
        "WikiArticle",
        "ContentPost",
        "BestPracticePost",
        "PorscheMomentPost",
        "Podcast",
        "FileEntry",
        "PALMS",
        "SlideShow",
        "Playlist",
        "SessionTemplate"];

export type PostTypes =
    | "TubePost"
    | "Quiz"
    | "WikiArticle"
    | "ContentPost"
    | "BestPracticePost"
    | "PorscheMomentPost"
    | "Podcast"
    | "FileEntry"
    | "PALMS"
    | "SlideShow"
    | "Playlist"
    | "SessionTemplate";

export type GlobalPostSearchState = {
    postType: PostTypes;
    search: string | undefined;
    filter: FetchPostsFilter;
    feFilter: FileEntrySearchFilter | undefined;
    results: SearchResultsDto<any> | undefined;
    token: { forward?: string; backward?: string };
    loading: boolean;
    selected: PostDownloadDto[] | undefined;
    cmsMode: "Project" | "File";
};

export type GlobalPostSearchAction =
    | { type: "BATCH"; actions: GlobalPostSearchAction[] }
    | { type: "SET_POST_TYPE"; postType: PostTypes }
    | { type: "SET_SEARCH"; search: string | undefined }
    | { type: "SET_FILTER"; filter: FetchPostsFilter }
    | { type: "SET_FE_FILTER"; feFilter: FileEntrySearchFilter | undefined }
    | { type: "SET_RESULTS"; results: SearchResultsDto<any> | undefined }
    | { type: "SET_TOKEN"; token: { forward?: string; backward?: string } }
    | { type: "SET_LOADING"; loading: boolean }
    | { type: "SET_SELECTED"; selected: PostDownloadDto[] | undefined }
    | { type: "SET_CMS_MODE"; cmsMode: "Project" | "File" };

export function globalPostSearchReducer(state: GlobalPostSearchState, action: GlobalPostSearchAction): GlobalPostSearchState {
    switch (action.type) {
        case "BATCH":
            return reducerBatchJob(state, action.actions, globalPostSearchReducer);
        case "SET_POST_TYPE":
            return reducerSetIfChanged(state, "postType", action.postType);
        case "SET_SEARCH":
            return reducerSetIfChanged(state, "search", action.search);
        case "SET_FILTER":
            return reducerSetIfChanged(state, "filter", action.filter);
        case "SET_FE_FILTER":
            return reducerSetIfChanged(state, "feFilter", action.feFilter);
        case "SET_RESULTS":
            return reducerSetIfChanged(state, "results", action.results);
        case "SET_TOKEN":
            return reducerSetIfChanged(state, "token", action.token);
        case "SET_LOADING":
            return reducerSetIfChanged(state, "loading", action.loading);
        case "SET_SELECTED": {
            const { cmsMode, postType } = state;
            if (postType === "FileEntry" && cmsMode === "Project" && action.selected !== undefined && action.selected.length > 0) {
                let nS = reducerSetIfChanged(state, "cmsMode", "File");
                nS = reducerSetIfChanged(nS, "search", "*" as string | undefined)
                nS = reducerSetIfChanged(nS, "filter", { ...nS.filter, onlyInProjectGroups: [action.selected[0].id], } as FetchPostsFilter);
                return nS;
            }
            return reducerSetIfChanged(state, "selected", action.selected);
        }

        case "SET_CMS_MODE":
            return reducerSetIfChanged(state, "cmsMode", action.cmsMode);
        default:
            return state;
    }
}

export const BATCH = (actions: GlobalPostSearchAction[]): GlobalPostSearchAction => ({ type: "BATCH", actions });
export const set_POST_TYPE = (postType: PostTypes): GlobalPostSearchAction => ({ type: "SET_POST_TYPE", postType });
export const set_SEARCH = (search: string | undefined): GlobalPostSearchAction => ({ type: "SET_SEARCH", search });
export const set_FILTER = (filter: FetchPostsFilter): GlobalPostSearchAction => ({ type: "SET_FILTER", filter });
export const set_FE_FILTER = (feFilter: FileEntrySearchFilter | undefined): GlobalPostSearchAction => ({ type: "SET_FE_FILTER", feFilter });
export const set_RESULTS = (results: SearchResultsDto<any> | undefined): GlobalPostSearchAction => ({ type: "SET_RESULTS", results });
export const set_TOKEN = (token: { forward?: string; backward?: string }): GlobalPostSearchAction => ({ type: "SET_TOKEN", token });
export const set_LOADING = (loading: boolean): GlobalPostSearchAction => ({ type: "SET_LOADING", loading });
export const set_SELECTED = (selected: PostDownloadDto[] | undefined): GlobalPostSearchAction => ({ type: "SET_SELECTED", selected });
export const set_CMS_MODE = (cmsMode: "Project" | "File"): GlobalPostSearchAction => ({ type: "SET_CMS_MODE", cmsMode });

export type GroupProps = {
    groupId: string;
};



export const getFilterComponent = (postType: PostTypes) => {
    switch (postType) {
        case "FileEntry":
            return CMSFilter;
        case "TubePost":
        case "Quiz":
        case "ContentPost":
        case "BestPracticePost":
        case "PorscheMomentPost":
        case "Podcast":
        case "SlideShow":
        case "Playlist":
            return StandardFilter; // Use standard filter for other post types
        case "WikiArticle":
        case "PALMS":
        default:
            return undefined; // Fallback
    }
};

export type CMSMode = "Project" | "File";

export const getSearchMethod = (postType: PostTypes, cmsMode: CMSMode) => {
    switch (postType) {
        case "FileEntry":
            return cmsMode === "File" ? CMSController.SearchFiles : CMSController.SearchProjects;
        case "WikiArticle":
            return WikiController.Search1;
        case "TubePost":
        case "Quiz":
        case "ContentPost":
        case "BestPracticePost":
        case "PorscheMomentPost":
        case "Podcast":
        case "SlideShow":
        case "Playlist":
            return PostController.SearchPostText3;
        case "PALMS":
            return PALMSController.CatalogSearch;
        default:
            return PostController.SearchPostText3; // Fallback
    }
};

export const getSuggestMethod = (postType: PostTypes, cmsMode: CMSMode) => {
    switch (postType) {
        case "FileEntry":
            return cmsMode === "File" ? CMSController.SuggestFiles : CMSController.SuggestProjects;
        case "WikiArticle":
            return WikiController.SuggestTerm;
        case "TubePost":
        case "Quiz":
        case "ContentPost":
        case "BestPracticePost":
        case "PorscheMomentPost":
        case "Podcast":
        case "SlideShow":
        case "Playlist":
            return PostController.SuggestPostTerm2;
        case "PALMS":
            return undefined;
        default:
            return PostController.SuggestPostTerm2; // Fallback
    }
};


export const Group = (p: GroupProps) => {
    const groups = useAppSelector(s => s.flatAdminGroups);
    let group = groups?.[p.groupId];
    return <div>{group?.name ?? ""}</div>
}

const getFirstLink = (f: FileEntrySearchResultDto) => {
    if (f.links === undefined || Object.keys(f.links).length === 0) {
        return undefined;
    }
    const content = f.links[Object.keys(f.links)?.[0]];
    if (content === undefined || Object.keys(content).length === 0) {
        return undefined;
    }
    return content[Object.keys(content)?.[0]];
}

const User = (p: { user: UserShortInfoDownloadDto }) => {
    const { user } = p;
    const { t } = useImgI18N("general");
    return <div>{user ? t("{{firstname}} {{secondname}}", { firstname: user.firstName, secondname: user.secondName }) : ""}</div>
}

const RenderMedia = (p: { post: PostDownloadDto }) => {
    const contentLanguage = useAppSelector(s => s.contentLanguage);
    const { post } = p;
    const url = React.useMemo(() => {
        switch (post.type ?? "") {
            case "TubePost": {
                const tp = post as TubePostDownloadDto;
                const c = _.find(tp.content, c => c.lng === contentLanguage) ?? _.find(tp.content, c => c.lng === "en-GB") ?? tp.content[0];
                return getThumbnailOrMediaLink(c?.content)?.uri ?? getThumbnailOrMediaLink(post.media)?.uri;
            }

            default:
                return getThumbnailOrMediaLink(post.media)?.uri;
        }
    }, [post, contentLanguage]);
    if (url) {
        return <div style={{ paddingTop: 5 }}><Image src={url} width="30px" squared shadowed /></div>
    }
    return <Icon name="empty" size="20px" />;
}

const RenderCMSFileType = (p: { post: FileEntrySearchResultDto }) => {
    const { post } = p;
    const { t } = useImgI18N("cms");
    const firstLink = getFirstLink(post);
    return <div>{firstLink?.mediaType ? t(firstLink.mediaType) : ""}</div>;

}

export const getColumns = (postType: PostTypes, cmsMode: CMSMode) => {
    switch (postType) {
        case "PALMS":
            return [
                {
                    elementName: "id",
                    width: 1,
                    filterFn: "text"
                },
                {
                    elementName: "title",
                    width: 3,
                    filterFn: "text",
                }

            ] as ColumnDefintion<PALMSModule>[];
        case "WikiArticle":
            return [
                {
                    elementName: "headline",
                    cell: (cell: any) => <SafeHTML html={cell.getValue()} />,
                    width: 1
                },
                {
                    elementName: "group_id",
                    header: "Group",
                    cell: (cell: any) => <Group groupId={cell.getValue()} />,
                    width: 1,
                },
            ] as any[];
        case "TubePost":
        case "Quiz":
        case "ContentPost":
        case "BestPracticePost":
        case "PorscheMomentPost":
        case "Podcast":
        case "SlideShow":
        case "Playlist":
            return [
                {
                    elementName: "media",
                    header: " ",
                    cell: (cell: any) => {
                        return <RenderMedia post={cell.row.original as PostDownloadDto} />
                    },
                    absWidth: 50,
                },
                // {
                //     elementName: "type",
                //     width: 1,
                // },
                {
                    elementName: "headlines",
                    columnCreator: "lngString",
                    width: 3,
                },
                {
                    elementName: "group_id",
                    header: "Group",
                    cell: (cell: any) => <Group groupId={cell.getValue()} />,
                    width: 1,
                },
                {
                    elementName: "created",
                    width: 1,
                    cell: (cell: CellContext<PostDownloadDto, any>) => {
                        const val = cell.getValue();
                        let d = "";
                        if (val) {
                            d = getTranslatedStandardDate(val, "Pp");
                        }
                        return <div style={{ textAlign: "left" }}>{d}</div>
                    }
                },
                {
                    elementName: "creator",
                    width: 1,
                    cell: (row: any) => {
                        const val = row.getValue();
                        return <User user={val} />
                    }
                },
            ] as ColumnDefintion<PostDownloadDto>[];
        case "FileEntry":
            return cmsMode === "File" ? [
                {
                    header: () => <div style={{ width: 50, textAlign: "center", marginLeft: -5 }} title="preview"><Icon name="thumbnail" /></div>,
                    elementName: "links",
                    id: "preview",
                    absWidth: 50,
                    align: "center",
                    cell: (cell) => {
                        const e = cell.row.original;
                        const m = cell.table.options.meta as (data: FileEntrySearchResultDto) => void;
                        if (e.id === "")
                            return <div />
                        return <UseCMSSearchRenderPeview data={e} onPreviewClicked={m} />
                    }
                },

                {
                    elementName: "name",
                    cell: (cell: CellContext<FileEntrySearchResultDto, any>) => {
                        const post = cell.row.original as FileEntrySearchResultDto;
                        const firstLink = getFirstLink(post);
                        const fn = firstLink?.fileName ?? "";
                        return <div>{fn}</div>;
                    },
                    width: 3
                },
                {
                    elementName: "links",
                    id: "lngs",
                    header: () => <div style={{ width: 60, textAlign: "center", marginLeft: -5 }} title="target content locations and languages"><Icon name="language" /></div>,
                    cell: (cell) => <UseCMSSearchRenderFileVersions data={cell.row.original} />,
                    noSort: true,
                    align: "center",
                    absWidth: 60,
                },
                {
                    elementName: "type",
                    cell: (cell: CellContext<FileEntrySearchResultDto, any>) => {
                        return <RenderCMSFileType post={cell.row.original as FileEntrySearchResultDto} />
                    },
                    width: 1
                },
                {
                    elementName: "projectGroupName",
                    width: 2,
                },


            ] as ColumnDefintion<FileEntrySearchResultDto>[]
                : [
                    {
                        elementName: "media",
                        header: " ",
                        cell: (cell: CellContext<ProjectGroupSearch, any>) => {
                            const post = cell.row.original as ProjectGroupSearch;
                            const url = getThumbnailOrMediaLink(post.media)?.uri;
                            if (url) {
                                return <div style={{ paddingTop: 5 }}><Image src={url} width="30px" squared shadowed /></div>
                            }
                            return <Icon name="empty" size="20px" />;
                        },
                        absWidth: 50,
                    },
                    {
                        elementName: "projectId",
                        width: 1,
                    },
                    {
                        elementName: "headlines",
                        columnCreator: "lngString",
                        width: 2
                    },
                    {
                        elementName: "groupName",
                        width: 1
                    },
                ] as ColumnDefintion<ProjectGroupSearch>[]
        default:
            return [];
    }
};

export const getInitFilter = (postType: PostTypes, excludeCategories?: string[]): FetchPostsFilter => {
    switch (postType) {
        case "Podcast":
            return { includedPostTypes: ["PodcastPost"], onlyFittingAllCategories: true, excludeCategories: excludeCategories };
        case "WikiArticle":
            return { includedPostTypes: ["WikiArticle"], onlyFittingAllCategories: true, excludeCategories: excludeCategories };
        case "TubePost":
        case "Quiz":
        case "ContentPost":
        case "BestPracticePost":
        case "PorscheMomentPost":
        case "FileEntry":
        case "PALMS":
        case "SlideShow":
        case "Playlist":
        case "SessionTemplate":
            return { includedPostTypes: [postType], onlyFittingAllCategories: true, excludeCategories: excludeCategories };
        default:
            return { includedPostTypes: [], onlyFittingAllCategories: true, excludeCategories: excludeCategories };
    }
};

